import { LOTTE_CATEGORY_FIELD } from '@/constants/lotte-api/category';
import { BuildingMenuType } from '@/enums';
import { getPoiListImageUrlFromContent } from '@/utils/poi/poiImage';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { BuildingInStoreInfo, BuildingMenu, FloorInfo, PoiContent } from '@/services/dto';
import type { ShopFilterConfig } from '@/stores/types/shopStore';
import type { PoiContentSlide } from '@/types/shop';
import poiUtils from '@/utils/poi';

export const getSearchMenu = (buildingMenus: Record<string, BuildingMenu[]> | null | undefined, language: string): BuildingMenu | undefined => {
    const menus = buildingMenus?.[language] ?? [];
    return menus.find((menu) => menu.menuType === BuildingMenuType.SEARCH);
};

export const resolveShopPoiContents = (searchMenu: BuildingMenu | undefined, selectedBuildingId: string | null, searchMode: SHOP_SEARCH_MODE): PoiContent[] => {
    if (!searchMenu) return [];

    const isFloorModeWithBuilding = searchMode === SHOP_SEARCH_MODE.SEARCH_BY_FLOOR && selectedBuildingId && selectedBuildingId !== 'all';

    if (isFloorModeWithBuilding && searchMenu.poiContentBuildings?.length) {
        const match = searchMenu.poiContentBuildings.find((b) => b.id === selectedBuildingId);
        return match?.poiContents ?? searchMenu.poiContents ?? [];
    }

    return searchMenu.poiContents ?? [];
};

export const convertPoiContentToSlide = (lang: string, buildings: BuildingInStoreInfo[], pois?: PoiContent[], searchText?: string): PoiContentSlide[] => {
    const buildingById = new Map(buildings.map((b) => [b.studioBuildingId, b]));

    const allPois: { poi: PoiContent; building: BuildingInStoreInfo }[] = pois
        ? pois.flatMap((poi) => {
              const building = buildingById.get(poi.studioBuildingId);
              return building ? [{ poi, building }] : [];
          })
        : [];

    return allPois.map(({ poi, building }) => {
        const contents = poi.contents;
        const managedContents = poi.managedContents;

        const formatData = {
            name: poi.name?.[lang] ? poi.name?.[lang] : poi.name?.['ko'],
            shopUrl: getPoiListImageUrlFromContent(poi, lang),
        };

        const name = formatData.name || '';
        const shopUrl = formatData.shopUrl || '';
        const floor = poiUtils.getFloorName(lang, poi.studioFloorId, building.floors ?? []);
        const buildingName = poiUtils.getBuildingLocalizedName(building, lang);
        const boothNumber = poi.boothNo || '';
        const poiCategoryCodes = poiUtils.getPoiCategoryCodes(poi);

        const rootFnbCategory = building.buildingCategories.find((c) => c.clientCode === LOTTE_CATEGORY_FIELD.SMALL_CLASSIFICATION_CODE);
        const rootFacilityCategory = building.buildingCategories.find((c) => c.clientCode === LOTTE_CATEGORY_FIELD.MEDIUM_CLASSIFICATION_CODE);
        const isFnb = poiCategoryCodes.includes(rootFnbCategory?.categoryCode || '');
        const isFacility = poiCategoryCodes.includes(rootFacilityCategory?.categoryCode || '');

        const finalCategory = isFnb
            ? building.filterCategories.fnb.find((f) => poiCategoryCodes.includes(f.categoryCode))
            : isFacility
              ? building.filterCategories.facility.find((f) => poiCategoryCodes.includes(f.categoryCode))
              : building.filterCategories.shop.find((f) => poiCategoryCodes.includes(f.categoryCode));

        const poiType = isFnb ? 'fnb' : isFacility ? 'facility' : 'normal';

        // Extract matching keyword if searchText is provided
        const matchingKeyword = searchText ? poiUtils.extractMatchingKeyword(contents.crltSrchKwrd || {}, searchText, lang) : undefined;

        return {
            id: poi.id,
            name,
            keyword: matchingKeyword,
            buildingName,
            boothNumber,
            floor,
            url: shopUrl,
            data: poi,
            poiType,
            category: finalCategory ?? undefined,
        };
    });
};

export const filterByFloor = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedBuildingId, storeCode, buildingCode, selectedFloor, buildings } = config;

    // Filter by storeCode and buildingCode using cstrCd.ko and cstrTownCd.ko
    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = shop.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = shop.data.contents.cstrTownCd?.ko || '';

        // Filter by storeCode (cstrTownCd.ko) and buildingCode (cstrCd.ko)
        if (poiCstrCd !== storeCode || poiCstrTownCd !== buildingCode) {
            return false;
        }

        // Now filter by floor using cstrFlrNm instead of studioFloorId
        if (selectedFloor?.id === 'all') {
            return true;
        }

        // Find building from storeCode and buildingCode to get floors

        const building = buildings?.find((b) => b.storeCode === storeCode && b.buildingCode === buildingCode);
        if (!building) return false;

        // Get floor name from POI content
        const poiFloorName = shop.data.contents.cstrFlrNm?.ko || poiUtils.getFloorName('ko', shop?.data?.studioFloorId, building.floors) || '';

        // Get selected floor name
        const selectedFloorInfo = selectedFloor?.data as FloorInfo;
        if (!selectedFloorInfo) return false;

        const selectedFloorName = selectedFloorInfo.name?.ko || '';

        // Compare floor names
        return poiFloorName === selectedFloorName;
    }

    // Fallback: Case: specific building selected (old logic)
    if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;

        if (selectedFloor?.id === 'all') {
            return shop.data.studioBuildingId === building.studioBuildingId;
        }

        return shop.data.studioFloorId === (selectedFloor?.data as FloorInfo)?.floor_code && shop.data.studioBuildingId === building.studioBuildingId;
    }

    // Case: all buildings mode
    if (selectedBuildingId === 'all') {
        if (selectedFloor?.id === 'all') return true;

        if (Array.isArray(selectedFloor?.data)) {
            return (selectedFloor.data as string[]).includes(shop.data.studioFloorId);
        }

        return false;
    }

    return false;
};

export const filterByCategory = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedCategory, storeCode, buildingCode, selectedBuildingId, buildings, categoryCodesForFilter } = config;

    if (!selectedCategory) return false;

    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = shop.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = shop.data.contents.cstrTownCd?.ko || '';

        if (poiCstrTownCd !== buildingCode || poiCstrCd !== storeCode) {
            return false;
        }
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (shop.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (selectedCategory.clientCode === 'all') {
        return true;
    }

    if (!categoryCodesForFilter?.length) return false;

    const poiCategoryCodes = poiUtils.getPoiCategoryCodes(shop.data);
    return categoryCodesForFilter.some((code) => poiCategoryCodes.includes(code));
};

export const filterByText = (shop: PoiContentSlide, config: ShopFilterConfig, keyboardLanguage: 'ko' | 'en'): boolean => {
    const { searchText, language, storeCode, buildingCode, selectedBuildingId, buildings, keywordSearchEnabled } = config;

    // Filter by storeCode and buildingCode using cstrCd.ko and cstrTownCd.ko
    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = shop.data?.contents?.cstrCd?.ko || '';
        const poiCstrTownCd = shop.data?.contents?.cstrTownCd?.ko || '';

        // Filter by storeCode (cstrTownCd.ko) and buildingCode (cstrCd.ko)
        if (poiCstrTownCd !== buildingCode || poiCstrCd !== storeCode) {
            return false;
        }
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        // Fallback: use selectedBuildingId if storeCode/buildingCode not available
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (shop.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (!searchText?.trim()) return true;

    const nameKeyboard = shop.data.name[keyboardLanguage] || '';
    const keywordKeyboard = (shop.data.contents?.crltSrchKwrd?.['ko'] || '').replaceAll('#', '');
    const nameDefault = shop.data.name[language] || '';
    const keywordDefault = (shop.data.contents?.crltSrchKwrd?.['ko'] || '').replaceAll('#', '');
    const normalizedSearchText = searchText.trim();

    if (keywordSearchEnabled) {
        return (
            poiUtils.searchByText(nameKeyboard, normalizedSearchText) ||
            poiUtils.searchByText(keywordKeyboard, normalizedSearchText) ||
            poiUtils.searchByText(nameDefault, normalizedSearchText) ||
            poiUtils.searchByText(keywordDefault, normalizedSearchText)
        );
    }

    return poiUtils.searchByText(nameKeyboard, normalizedSearchText) || poiUtils.searchByText(nameDefault, normalizedSearchText);
};

export const createShopFilter = (searchMode: SHOP_SEARCH_MODE, config: ShopFilterConfig, keyboardLanguage: 'ko' | 'en') => {
    const filterMap = {
        [SHOP_SEARCH_MODE.SEARCH_BY_FLOOR]: filterByFloor,
        [SHOP_SEARCH_MODE.SEARCH_BY_CATEGORY]: filterByCategory,
        [SHOP_SEARCH_MODE.SEARCH_BY_TEXT]: filterByText,
    };

    const filterFunction = filterMap[searchMode];

    return (shop: PoiContentSlide) => {
        return filterFunction ? filterFunction(shop, config, keyboardLanguage) : true;
    };
};

const shopUtils = {
    convertPoiContentToSlide,
    createShopFilter,
    filterByFloor,
    filterByCategory,
    filterByText,
    getSearchMenu,
    resolveShopPoiContents,
};

export default shopUtils;
